<template>
  <BaseCard title="Datos Personales" :noHover="true" :collapsible="true">
    <div class="actions-internal">
      <button class="btn-action" @click="toggleEdit">
        {{ isEditing ? 'Guardar' : 'Editar' }}
      </button>
    </div>

    <!-- MODO VISUALIZACIÓN (Nuevo Diseño) -->
    <div v-if="!isEditing" class="datos-grid">
      <!-- Fila 1: Nombre y CI (2 columnas) -->
      <div class="dato-item">
        <span class="label">Nombre</span>
        <span class="valor">{{ paciente.nombre }}</span>
      </div>
      <div class="dato-item">
        <span class="label">CI/RUT</span>
        <span class="valor">{{ paciente.ci || 'Sin documento' }}</span>
      </div>

      <!-- Filas siguientes: Ancho completo -->
      <div class="dato-item full-width">
        <span class="label">Teléfono</span>
        <span class="valor">{{ paciente.telefono || 'Sin teléfono' }}</span>
      </div>

      <div class="dato-item full-width">
        <span class="label">Fecha de Nacimiento</span>
        <span class="valor" :class="{ empty: !nacimientoTexto }">
          {{ nacimientoTexto || 'Sin fecha de nacimiento' }}
        </span>
      </div>

      <div class="dato-item full-width">
        <span class="label">Mutualista</span>
        <span class="valor" :class="{ empty: !paciente.mutualista }">
          {{ paciente.mutualista || 'Sin mutualista' }}
        </span>
      </div>

    </div>

    <!-- MODO EDICIÓN -->
    <div v-else class="edit-form">
      <div class="form-grid">
        <!-- autocomplete="off": son datos del PACIENTE, no de la doctora. Sin esto el navegador
             los guarda en su autofill y se los sugiere al cargar el siguiente paciente. -->
        <div class="form-group">
          <label for="pac-nombre" :class="{ error: errors.nombre }">Nombre</label>
          <input v-model="nombre" id="pac-nombre" name="pac-nombre" autocomplete="off" :class="{ error: errors.nombre }" />
        </div>
        <div class="form-group">
          <label for="pac-apellido">Apellido</label>
          <input v-model="apellido" id="pac-apellido" name="pac-apellido" autocomplete="off" />
        </div>
        <div class="form-group">
          <label for="pac-ci" :class="{ error: errors.ci }">CI/RUT *</label>
          <input v-model="ci" id="pac-ci" name="pac-ci" autocomplete="off" :class="{ error: errors.ci }" />
        </div>
        <div class="form-group">
          <label for="pac-telefono" :class="{ error: errors.telefono }">Teléfono *</label>
          <input v-model="telefono" id="pac-telefono" name="pac-telefono" autocomplete="off" :class="{ error: errors.telefono }" />
        </div>
        <div class="form-group">
          <label :for="modoNacimiento === 'fecha' ? 'pac-nacimiento' : 'pac-edad'">Fecha de Nacimiento</label>
          <div class="toggle-nac">
            <button type="button" :class="{ activo: modoNacimiento === 'fecha' }" @click="modoNacimiento = 'fecha'">Fecha</button>
            <button type="button" :class="{ activo: modoNacimiento === 'edad' }" @click="modoNacimiento = 'edad'">Edad</button>
          </div>
          <input v-if="modoNacimiento === 'fecha'" type="date" v-model="fechaNacimiento" id="pac-nacimiento" name="pac-nacimiento" autocomplete="off" />
          <input v-else type="number" min="0" max="120" v-model="edad" id="pac-edad" name="pac-edad" autocomplete="off" placeholder="Ej: 45" />
        </div>
        <div class="form-group">
          <label for="pac-mutualista">Mutualista</label>
          <input v-model="mutualista" id="pac-mutualista" name="pac-mutualista" autocomplete="off" placeholder="Ej: CASMU, Española, ASSE…" />
        </div>
      </div>
    </div>
  </BaseCard>

  <!-- Modal de pago pendiente -->
  <ModalPagoPendiente
    :mostrar="mostrarModalPago"
    @cerrar="cerrarModalPago"
  />
  <!-- Los avisos salen por el diálogo global (ConfirmDialog en App.vue). -->
</template>

<script setup>
import BaseCard from "./BaseCard.vue";
import ModalPagoPendiente from "@/components/ModalPagoPendiente.vue";
import { ref, computed, watch, reactive, onUnmounted } from "vue";
import { usePacientesStore } from "@/stores/pacientes";
import { usePagoVerificacion } from "@/composables/usePagoVerificacion";
import { marcarEdicion } from "@/composables/edicionPendiente";
import { separarNombreApellido } from "@/utils/nombrePaciente";
import { useDialog } from "@/composables/useDialog";

const props = defineProps({
  paciente: {
    type: Object,
    required: true,
  },
});

// no emits used — inline edit saves directly to store

const store = usePacientesStore();
const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();
const { alerta } = useDialog();

const isEditing = ref(false);

// Mientras se editan los datos del paciente, marcar edición pendiente para que la
// auto-actualización no recargue la app y pierda los cambios a medias.
watch(isEditing, (v) => marcarEdicion("datos-paciente", v));
onUnmounted(() => marcarEdicion("datos-paciente", false));

const nombre = ref("");
const apellido = ref("");
const ci = ref("");
const telefono = ref("");
const fechaNacimiento = ref("");
// modoNacimiento: 'fecha' (calendario exacto) o 'edad' (el paciente solo sabe la edad).
// En modo 'edad' NO guardamos una fecha real: convertimos la edad a un año de nacimiento
// aproximado (1° de enero) y marcamos edadAproximada para mostrarla como estimada.
const modoNacimiento = ref("fecha");
const edad = ref("");
const mutualista = ref("");
const errors = reactive({});

watch(
  () => props.paciente,
  (p) => {
    if (p) {
      // Con apellido persistido se recupera exacto; sin él (pacientes viejos),
      // el helper cae al split por último espacio de siempre.
      const sep = separarNombreApellido(p.nombre, p.apellido);
      nombre.value = sep.nombre;
      apellido.value = sep.apellido;

      ci.value = p.ci || "";
      telefono.value = p.telefono || "";
      fechaNacimiento.value = p.fechaNacimiento || "";
      mutualista.value = p.mutualista || "";
      // Si el paciente se cargó por edad aproximada, abrir el formulario en ese modo
      // con la edad ya calculada; si no, en modo fecha exacta (el de siempre).
      if (p.edadAproximada && p.fechaNacimiento) {
        modoNacimiento.value = "edad";
        edad.value = String(calcularEdad(p.fechaNacimiento));
      } else {
        modoNacimiento.value = "fecha";
        edad.value = "";
      }
    }
  },
  { immediate: true }
);

function toggleEdit() {
  if (isEditing.value) {
    // Intentando guardar: Validar
    errors.nombre = !nombre.value;
    errors.ci = !ci.value;
    errors.telefono = !telefono.value;

    if (errors.nombre || errors.ci || errors.telefono) {
      return; // No guardar si hay errores
    }

    const apellidoTrim = apellido.value.trim();
    const nombreCompleto = `${nombre.value.trim()} ${apellidoTrim}`.trim();

    // Resolver fecha de nacimiento según el modo elegido.
    let fechaNacFinal = "";
    let edadAproximada = false;
    if (modoNacimiento.value === "edad") {
      const e = parseInt(edad.value, 10);
      if (!isNaN(e) && e >= 0 && e <= 120) {
        // Edad -> año de nacimiento aproximado (asumimos cumpleaños a inicio de año).
        // Guardarlo como fecha permite que la edad se siga actualizando sola con el tiempo.
        fechaNacFinal = `${new Date().getFullYear() - e}-01-01`;
        edadAproximada = true;
      }
    } else {
      fechaNacFinal = fechaNacimiento.value;
    }

    try {
      store.editarPaciente(props.paciente.id, {
        nombre: nombreCompleto,
        apellido: apellidoTrim,
        ci: ci.value.trim(),
        telefono: telefono.value,
        fechaNacimiento: fechaNacFinal,
        edadAproximada,
        mutualista: mutualista.value.trim(),
      });
      isEditing.value = false;
    } catch (error) {
      alerta(error.message);
    }
  } else {
    // Intentando activar modo edición: verificar pago
    if (!verificarPermisoPago()) {
      return;
    }
    isEditing.value = true;
  }
}

// Edad en años cumplidos a partir de una fecha "YYYY-MM-DD".
function calcularEdad(fecha) {
  const [anio, mes, dia] = fecha.split('-');
  const hoy = new Date();
  let edad = hoy.getFullYear() - parseInt(anio);
  const mHoy = hoy.getMonth() + 1;
  const dHoy = hoy.getDate();
  if (parseInt(mes) > mHoy || (parseInt(mes) === mHoy && parseInt(dia) > dHoy)) {
    edad--;
  }
  return edad;
}

function formatearFecha(fecha) {
  if (!fecha) return '';
  const [anio, mes, dia] = fecha.split('-');
  return `${dia}/${mes}/${anio} (${calcularEdad(fecha)} años)`;
}

// Texto de la fila Nacimiento en modo lectura: fecha exacta con edad, o edad
// estimada (sin fecha, con "~") cuando el paciente se cargó solo por edad.
const nacimientoTexto = computed(() => {
  const p = props.paciente;
  if (!p?.fechaNacimiento) return '';
  if (p.edadAproximada) return `${calcularEdad(p.fechaNacimiento)} años`;
  return formatearFecha(p.fechaNacimiento);
});
</script>

<style scoped>
/* GRID DE DATOS (Visualización) */
.datos-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.dato-item {
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.full-width {
  grid-column: 1 / -1;
}

.label {
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: system-ui, sans-serif;
}

.valor {
  color: #111827;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  font-family: serif;
}

.valor.empty {
  color: #9ca3af;
  font-style: italic;
  font-weight: 400;
}

/* ESTILOS FORMULARIO (Edición) */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.form-group input,
.form-group textarea {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e6e6e6;
  outline: none;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #678AB2;
}

.form-group input.error {
  border-color: #dc2626;
}
.form-group label.error {
  color: #dc2626;
}

/* Toggle Fecha exacta / Edad aprox. */
.toggle-nac {
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
}

.toggle-nac button {
  flex: 1;
  padding: 5px 6px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.toggle-nac button.activo {
  background: #678AB2;
  border-color: #678AB2;
  color: white;
}

.actions-internal {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.btn-action {
  background: #eaf1ff;
  color: #678AB2;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.2s;
}
.btn-action:hover {
  background: #dbeafe;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .datos-grid {
    grid-template-columns: 1fr;
  }
  
  .valor {
    font-size: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

/* Estilos específicos para mejorar el encabezado en la ficha */
:deep(.card) {
  padding: 0 !important;
  overflow: hidden;
}

:deep(.card-header) {
  background-color: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0 !important;
}

:deep(.card-body) {
  padding: 16px;
}
</style>
